import { initializeChakraComponent } from 'Shared/chakra-initialize';
import {
   CharacterCount,
   IsFocused,
   SessionStorage,
   OriginalState,
   SubmitError,
} from './extensions';
import { CharacterCount as CharacterCountComponent } from './components/CharacterCount';
import * as React from 'react';
import { useEditor, EditorContent, Editor, Extension, Mark, Node } from '@tiptap/react';
import { Box } from '@chakra-ui/react';
import { CommentsExtensions, CommentsMenuBar } from './presets/comments';
import { GuideIntroExtensions, GuideIntroMenuBar } from './presets/guide_intro';
import { AnswersExtensions, AnswersMenuBar } from './presets/answers';
import { setEditor } from './editor_cache';
import { SubmitErrorComponent } from './components/SubmitError';

type EditorPreset = 'comments' | 'guide_intro' | 'answers';
type TiptapExtension = Extension | Mark | Node;
export type { TiptapExtension };

const MenuBar = (props: { editor: Editor; preset: EditorPreset }) => {
   const { editor, preset } = props;

   if (!editor) {
      return null;
   }

   switch (preset) {
      case 'comments':
         return <CommentsMenuBar editor={editor} />;
      case 'guide_intro':
         return <GuideIntroMenuBar editor={editor} />;
      case 'answers':
         return <AnswersMenuBar editor={editor} />;
      default:
         throw new Error(`Invalid preset: ${preset}`);
   }
};

const getExtensionsForPreset = (preset: EditorPreset): Array<TiptapExtension> => {
   switch (preset) {
      case 'comments':
         return CommentsExtensions;
      case 'guide_intro':
         return GuideIntroExtensions;
      case 'answers':
         return AnswersExtensions;
      default:
         throw new Error(`Invalid preset: ${preset}`);
   }
};

export const parseEditorContent = (text: string) => {
   if (!text) {
      return '';
   }
   try {
      return JSON.parse(text);
   } catch (e) {
      return text;
   }
};

export const TiptapEditor = (props: {
   editorid: string;
   text?: string;
   preset: EditorPreset;
   useCharacterCount?: boolean;
   maxCharacters?: number;
}) => {
   const { editorid, text = '', preset, useCharacterCount = true, maxCharacters = 1024 } = props;

   const extensions = [
      ...getExtensionsForPreset(preset),
      IsFocused,
      SessionStorage.configure({
         editorId: editorid,
         autosaveWait: 200,
      }),
      OriginalState.configure({
         savedContent: text,
         editorid,
      }),
      SubmitError.configure({}),
   ];

   if (useCharacterCount) {
      extensions.push(
         CharacterCount.configure({
            limit: maxCharacters,
         })
      );
   }

   const editor = useEditor({
      extensions,
      content: parseEditorContent(text),
   });

   const isFocused = editor && editor.storage.isFocused.isFocused;
   const submitError = editor?.storage.submitError.error;

   React.useEffect(() => {
      setEditor(editorid, editor);
   }, [editor]);

   return (
      <>
         <Box id={editorid} className="ProseMirror-menubar-wrapper">
            <MenuBar editor={editor} preset={preset} />
            <EditorContent editor={editor} />
         </Box>
         {submitError ? <SubmitErrorComponent error={submitError} editor={editor} /> : null}
         {isFocused && useCharacterCount ? (
            <CharacterCountComponent editor={editor} {...props} />
         ) : null}
      </>
   );
};

initializeChakraComponent('TiptapEditor', TiptapEditor);
